import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import yaml from 'js-yaml';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { apiClient } from '../../lib/api-client';
import type { RoleSummary } from './LeafConfigCard';
import { ResolvedWorkflowTaxonomyReadonly } from './ResolvedWorkflowTaxonomyReadonly';
import { TaxonomyToolsEditor, type TaxonomyToolsValue } from './TaxonomyToolsEditor';

interface DefEntry {
  id: string;
  displayName?: string;
}

interface TenantDefEntry {
  code: string;
  displayName?: string;
}

interface StreamDefs {
  products: DefEntry[];
  teams: DefEntry[];
  tenants: TenantDefEntry[];
}

function parseStreamDefinitions(yamlStr: string): StreamDefs {
  try {
    const doc = yaml.load(yamlStr) as any;
    const root = doc?.stream || doc;
    const products = (root.productDefinitions || [])
      .map((p: any) => ({
        id: String(p?.id || '').trim(),
        displayName: p?.displayName != null ? String(p.displayName) : undefined,
      }))
      .filter((p: DefEntry) => p.id);
    const teams = (root.teamDefinitions || [])
      .map((t: any) => ({
        id: String(t?.id || '').trim(),
        displayName: t?.displayName != null ? String(t.displayName) : undefined,
      }))
      .filter((t: DefEntry) => t.id);
    const tenants = (root.tenantDefinitions || [])
      .map((t: any) => ({
        code: String(t?.code || '').trim(),
        displayName: t?.displayName != null ? String(t.displayName) : undefined,
      }))
      .filter((t: TenantDefEntry) => t.code);
    return { products, teams, tenants };
  } catch {
    return { products: [], teams: [], tenants: [] };
  }
}

function roleToYamlSnippet(role: Record<string, unknown>): string {
  return yaml.dump({ stream: { roles: [role] } }, { lineWidth: 120, noRefs: true, skipInvalid: true });
}

function parseRoleFromYaml(yamlStr: string): Record<string, unknown> {
  const parsed = yaml.load(yamlStr) as any;
  if (parsed?.stream?.roles?.[0]) return parsed.stream.roles[0] as Record<string, unknown>;
  if (parsed && typeof parsed === 'object' && 'code' in parsed) return parsed as Record<string, unknown>;
  throw new Error('YAML must be a single role object or { stream: { roles: [ {...} ] } }');
}

interface RoleConfigEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  role: RoleSummary | null;
}

type EditorMode = 'form' | 'yaml' | 'resolved';

export function RoleConfigEditDialog({ open, onClose, onSaved, role }: RoleConfigEditDialogProps) {
  const [mode, setMode] = useState<EditorMode>('form');
  const [defs, setDefs] = useState<StreamDefs>({ products: [], teams: [], tenants: [] });
  const [yamlContent, setYamlContent] = useState('');
  const [form, setForm] = useState<Record<string, any>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [resolved, setResolved] = useState<{
    overlay: any;
    replace?: any;
    effectiveToolsByWorkflowType?: { overlay: Record<string, any[]>; replace: Record<string, any[]> };
  } | null>(null);
  const [resolvedLoading, setResolvedLoading] = useState(false);
  const [resolvedError, setResolvedError] = useState<string | null>(null);

  const streamId = role?.streamId || '';
  const roleCode = role?.code || '';

  const load = useCallback(async () => {
    if (!streamId || !roleCode) return;
    setLoading(true);
    setResolved(null);
    setResolvedError(null);
    setParseError(null);
    setSaveError(null);
    try {
      const [roleRes, streamRes] = await Promise.all([
        apiClient.get<{ role: Record<string, unknown> }>(
          `/api/admin/ai-config/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}`,
        ),
        apiClient.get<{ yaml: string }>(`/api/admin/ai-config/streams/${encodeURIComponent(streamId)}`),
      ]);
      const r = roleRes.role || {};
      setForm(JSON.parse(JSON.stringify(r)));
      setYamlContent(roleToYamlSnippet(r));
      setDefs(parseStreamDefinitions(streamRes.yaml || ''));
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : 'Failed to load role');
    } finally {
      setLoading(false);
    }
  }, [streamId, roleCode]);

  useEffect(() => {
    if (open && streamId && roleCode) {
      setMode('form');
      void load();
    }
  }, [open, streamId, roleCode, load]);

  const formResolveKey = useMemo(
    () =>
      JSON.stringify({
        taxonomy: form.taxonomy ?? null,
        tools: form.tools ?? null,
        prompts: form.prompts ?? null,
        products: form.products ?? null,
        teams: form.teams ?? null,
        tenants: form.tenants ?? null,
        taxonomyWorkflowTypeIds: form.taxonomyWorkflowTypeIds ?? null,
      }),
    [
      form.taxonomy,
      form.tools,
      form.prompts,
      form.products,
      form.teams,
      form.tenants,
      form.taxonomyWorkflowTypeIds,
    ],
  );

  useEffect(() => {
    if (!open || mode !== 'resolved' || !streamId || !roleCode || loading) return;
    let cancelled = false;
    const run = async () => {
      setResolvedLoading(true);
      setResolvedError(null);
      try {
        const rawTax = form.taxonomy as { workflowTypes?: unknown } | undefined;
        const taxonomy =
          rawTax && typeof rawTax === 'object' && Array.isArray(rawTax.workflowTypes)
            ? rawTax
            : { workflowTypes: [] };
        const draftConfig: Record<string, unknown> = {
          taxonomy,
          tools: Array.isArray(form.tools) ? form.tools : [],
          prompts: form.prompts && typeof form.prompts === 'object' ? form.prompts : {},
          products: Array.isArray(form.products) ? form.products : [],
          teams: Array.isArray(form.teams) ? form.teams : [],
          tenants: Array.isArray(form.tenants) ? form.tenants : [],
        };
        if (Array.isArray(form.taxonomyWorkflowTypeIds)) {
          draftConfig.taxonomyWorkflowTypeIds = form.taxonomyWorkflowTypeIds;
        }
        const res = await apiClient.previewAiConfigResolve({
          streamId,
          entityType: 'role',
          entityId: roleCode,
          draftConfig,
        });
        if (!cancelled) setResolved(res);
      } catch (e: unknown) {
        if (!cancelled) setResolvedError(e instanceof Error ? e.message : 'Preview failed');
      } finally {
        if (!cancelled) setResolvedLoading(false);
      }
    };
    const t = setTimeout(() => void run(), 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, mode, streamId, roleCode, loading, formResolveKey]);

  const productIds = useMemo(() => (Array.isArray(form.products) ? (form.products as string[]) : []), [form.products]);
  const teamIds = useMemo(() => (Array.isArray(form.teams) ? (form.teams as string[]) : []), [form.teams]);
  const tenantCodes = useMemo(() => (Array.isArray(form.tenants) ? (form.tenants as string[]) : []), [form.tenants]);

  const unknownProductIds = useMemo(
    () => productIds.filter((id) => id.trim() && !defs.products.some((p) => p.id === id.trim())),
    [productIds, defs.products],
  );
  const unknownTeamIds = useMemo(
    () => teamIds.filter((id) => id.trim() && !defs.teams.some((t) => t.id === id.trim())),
    [teamIds, defs.teams],
  );
  const unknownTenantCodes = useMemo(
    () => tenantCodes.filter((c) => c.trim() && !defs.tenants.some((t) => t.code === c.trim())),
    [tenantCodes, defs.tenants],
  );

  const addProductRow = () => {
    setForm((f) => ({ ...f, products: [...(Array.isArray(f.products) ? f.products : []), ''] }));
  };
  const updateProductRow = (idx: number, id: string) => {
    setForm((f) => {
      const arr = [...(Array.isArray(f.products) ? f.products : [])];
      arr[idx] = id;
      return { ...f, products: arr };
    });
  };
  const removeProductRow = (idx: number) => {
    setForm((f) => {
      const arr = [...(Array.isArray(f.products) ? f.products : [])];
      arr.splice(idx, 1);
      return { ...f, products: arr };
    });
  };

  const addTeamRow = () => {
    setForm((f) => ({ ...f, teams: [...(Array.isArray(f.teams) ? f.teams : []), ''] }));
  };
  const updateTeamRow = (idx: number, id: string) => {
    setForm((f) => {
      const arr = [...(Array.isArray(f.teams) ? f.teams : [])];
      arr[idx] = id;
      return { ...f, teams: arr };
    });
  };
  const removeTeamRow = (idx: number) => {
    setForm((f) => {
      const arr = [...(Array.isArray(f.teams) ? f.teams : [])];
      arr.splice(idx, 1);
      return { ...f, teams: arr };
    });
  };

  const addTenantRow = () => {
    setForm((f) => ({ ...f, tenants: [...(Array.isArray(f.tenants) ? f.tenants : []), ''] }));
  };
  const updateTenantRow = (idx: number, code: string) => {
    setForm((f) => {
      const arr = [...(Array.isArray(f.tenants) ? f.tenants : [])];
      arr[idx] = code;
      return { ...f, tenants: arr };
    });
  };
  const removeTenantRow = (idx: number) => {
    setForm((f) => {
      const arr = [...(Array.isArray(f.tenants) ? f.tenants : [])];
      arr.splice(idx, 1);
      return { ...f, tenants: arr };
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      if (mode === 'yaml') {
        const roleObj = parseRoleFromYaml(yamlContent);
        const c = String(roleObj.code || '');
        if (c !== roleCode) {
          setSaveError(`Role code in YAML (${c || 'missing'}) must match ${roleCode}`);
          setSaving(false);
          return;
        }
        await apiClient.put(
          `/api/admin/ai-config/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/full`,
          roleObj,
        );
      } else {
        const patch: Record<string, unknown> = {
          agentTitle: form.agentTitle,
          agentDescription: form.agentDescription,
          enabled: Boolean(form.enabled),
          products: productIds.map((s) => s.trim()).filter(Boolean),
          teams: teamIds.map((s) => s.trim()).filter(Boolean),
          tenants: tenantCodes.map((s) => s.trim()).filter(Boolean),
          adoProject: form.adoProject,
          adoAreaPath: form.adoAreaPath || undefined,
          repo: form.repo,
          teamsTeamName: form.teamsTeamName || undefined,
          groupChatName: form.groupChatName || undefined,
          parentRef: form.parentRef || undefined,
          bot: form.bot && typeof form.bot === 'object' ? form.bot : { enabled: false, displayName: '' },
        };
        if (Array.isArray(form.taxonomyWorkflowTypeIds)) {
          patch.taxonomyWorkflowTypeIds = (form.taxonomyWorkflowTypeIds as string[])
            .map((s) => String(s).trim())
            .filter(Boolean);
        }
        if (Array.isArray(form.taxonomy?.workflowTypes)) {
          patch.taxonomy = form.taxonomy;
        }
        if (Array.isArray(form.tools)) {
          patch.tools = form.tools;
        }
        if (form.prompts && typeof form.prompts === 'object') {
          patch.prompts = form.prompts;
        }
        await apiClient.put(
          `/api/admin/ai-config/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}`,
          patch,
        );
      }
      onSaved();
      onClose();
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const switchMode = (next: EditorMode) => {
    if (next === mode) return;

    if (next === 'yaml') {
      try {
        setYamlContent(roleToYamlSnippet(form));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    if (mode === 'yaml' && next !== 'yaml') {
      try {
        const r = parseRoleFromYaml(yamlContent);
        setForm(JSON.parse(JSON.stringify(r)));
        setParseError(null);
      } catch (e: any) {
        setParseError(e.message);
        return;
      }
    }

    setMode(next);
  };

  if (!role || !role.code) return null;

  const overlayTools = resolved?.overlay?.tools as any[] | undefined;
  const overlayTax = resolved?.overlay?.taxonomy;
  const overlayEffectiveToolsByWorkflowType = resolved?.effectiveToolsByWorkflowType?.overlay as Record<string, any[]> | undefined;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <DialogTitle>Edit role: {role.agentTitle || role.code}</DialogTitle>
            <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => switchMode('form')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'form'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                Form
              </button>
              <button
                type="button"
                onClick={() => switchMode('yaml')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'yaml'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                YAML
              </button>
              <button
                type="button"
                onClick={() => switchMode('resolved')}
                className={`px-3 py-1.5 text-xs font-medium ${
                  mode === 'resolved'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                Resolved
              </button>
            </div>
          </div>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {parseError && mode !== 'resolved' && (
            <p className="text-sm text-red-600 dark:text-red-400">{parseError}</p>
          )}
          {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}

          {mode === 'form' && !loading && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Code</span>
                  <Input value={String(form.code || roleCode)} disabled className="font-mono text-xs" />
                </label>
                <label className="space-y-1 flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    checked={Boolean(form.enabled)}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Enabled</span>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Agent title</span>
                  <Input
                    value={String(form.agentTitle || '')}
                    onChange={(e) => setForm((f) => ({ ...f, agentTitle: e.target.value }))}
                  />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Description</span>
                  <Input
                    value={String(form.agentDescription || '')}
                    onChange={(e) => setForm((f) => ({ ...f, agentDescription: e.target.value }))}
                  />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Parent ref</span>
                  <Input
                    value={String(form.parentRef || '')}
                    onChange={(e) => setForm((f) => ({ ...f, parentRef: e.target.value }))}
                    placeholder="e.g. management/chief-technology-officer"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="space-y-2 border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 bg-white dark:bg-gray-900/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Products</span>
                    <button
                      type="button"
                      onClick={addProductRow}
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                  {productIds.length === 0 && (
                    <p className="text-xs text-gray-500">No product ids — add a row or leave empty.</p>
                  )}
                  {productIds.map((pid, idx) => {
                    const label = defs.products.find((p) => p.id === pid.trim())?.displayName;
                    return (
                      <div key={`product-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-center">
                        <Input
                          value={pid}
                          onChange={(e) => updateProductRow(idx, e.target.value)}
                          placeholder="id"
                          className="text-xs !py-1 font-mono"
                        />
                        <Input
                          value={label || ''}
                          readOnly
                          disabled
                          placeholder="display name"
                          className="text-xs !py-1 bg-gray-50 dark:bg-gray-800/50"
                        />
                        <button
                          type="button"
                          onClick={() => removeProductRow(idx)}
                          className="text-red-500 hover:text-red-700 px-1"
                          aria-label="Remove product"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {unknownProductIds.length > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Unknown product ids (not in stream definitions): {unknownProductIds.join(', ')}
                    </p>
                  )}
                </div>

                <div className="space-y-2 border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 bg-white dark:bg-gray-900/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Teams</span>
                    <button
                      type="button"
                      onClick={addTeamRow}
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                  {teamIds.length === 0 && (
                    <p className="text-xs text-gray-500">No team ids — add a row or leave empty.</p>
                  )}
                  {teamIds.map((tid, idx) => {
                    const label = defs.teams.find((t) => t.id === tid.trim())?.displayName;
                    return (
                      <div key={`team-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-center">
                        <Input
                          value={tid}
                          onChange={(e) => updateTeamRow(idx, e.target.value)}
                          placeholder="id"
                          className="text-xs !py-1 font-mono"
                        />
                        <Input
                          value={label || ''}
                          readOnly
                          disabled
                          placeholder="display name"
                          className="text-xs !py-1 bg-gray-50 dark:bg-gray-800/50"
                        />
                        <button
                          type="button"
                          onClick={() => removeTeamRow(idx)}
                          className="text-red-500 hover:text-red-700 px-1"
                          aria-label="Remove team"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {unknownTeamIds.length > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Unknown team ids (not in stream definitions): {unknownTeamIds.join(', ')}
                    </p>
                  )}
                </div>

                <div className="space-y-2 border border-gray-100 dark:border-gray-700/50 rounded-md p-2.5 bg-white dark:bg-gray-900/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Tenants</span>
                    <button
                      type="button"
                      onClick={addTenantRow}
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                  {tenantCodes.length === 0 && (
                    <p className="text-xs text-gray-500">No tenant codes — add a row or leave empty.</p>
                  )}
                  {tenantCodes.map((code, idx) => {
                    const label = defs.tenants.find((t) => t.code === code.trim())?.displayName;
                    return (
                      <div key={`tenant-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-center">
                        <Input
                          value={code}
                          onChange={(e) => updateTenantRow(idx, e.target.value)}
                          placeholder="code"
                          className="text-xs !py-1 font-mono"
                        />
                        <Input
                          value={label || ''}
                          readOnly
                          disabled
                          placeholder="display name"
                          className="text-xs !py-1 bg-gray-50 dark:bg-gray-800/50"
                        />
                        <button
                          type="button"
                          onClick={() => removeTenantRow(idx)}
                          className="text-red-500 hover:text-red-700 px-1"
                          aria-label="Remove tenant"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  {unknownTenantCodes.length > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Unknown tenant codes (not in stream definitions): {unknownTenantCodes.join(', ')}
                    </p>
                  )}
                </div>
              </div>

              <label className="space-y-1 block">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Taxonomy workflow types (optional)
                </span>
                <Input
                  value={
                    Array.isArray(form.taxonomyWorkflowTypeIds)
                      ? (form.taxonomyWorkflowTypeIds as string[]).join(', ')
                      : ''
                  }
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      taxonomyWorkflowTypeIds: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    }))
                  }
                  placeholder="Comma-separated ids, e.g. cross-functional-decision-brief"
                  className="text-xs font-mono"
                />
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Stream defines workflow types; each assigned product/team/tenant enables a subset (ids list or
                  inline <span className="font-mono">taxonomy.workflowTypes</span>). The role sees the intersection
                  across those rows. When set here, this list further restricts that result. Leave empty for no extra
                  restriction.
                </p>
              </label>

              <div className="border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Refine taxonomy prompt behavior and tool access exactly like product/team/tenant definitions.
                </p>
                <TaxonomyToolsEditor
                  open={open}
                  streamId={streamId}
                  entityType="role"
                  entityId={roleCode}
                  value={{
                    tools: Array.isArray(form.tools) ? form.tools : [],
                    prompts: form.prompts && typeof form.prompts === 'object' ? form.prompts : {},
                    workflowTypes: Array.isArray(form.taxonomy?.workflowTypes) ? form.taxonomy.workflowTypes : [],
                  } as TaxonomyToolsValue}
                  onChange={(next) =>
                    setForm((f) => ({
                      ...f,
                      tools: next.tools,
                      prompts: next.prompts,
                      taxonomy: { workflowTypes: next.workflowTypes },
                    }))
                  }
                  draftConfigExtras={{
                    products: productIds.map((s) => s.trim()).filter(Boolean),
                    teams: teamIds.map((s) => s.trim()).filter(Boolean),
                    tenants: tenantCodes.map((s) => s.trim()).filter(Boolean),
                    taxonomyWorkflowTypeIds: Array.isArray(form.taxonomyWorkflowTypeIds)
                      ? form.taxonomyWorkflowTypeIds
                      : [],
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">ADO project</span>
                  <Input
                    value={String(form.adoProject || '')}
                    onChange={(e) => setForm((f) => ({ ...f, adoProject: e.target.value }))}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">ADO area path</span>
                  <Input
                    value={String(form.adoAreaPath || '')}
                    onChange={(e) => setForm((f) => ({ ...f, adoAreaPath: e.target.value }))}
                  />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Repository</span>
                  <Input
                    value={String(form.repo || '')}
                    onChange={(e) => setForm((f) => ({ ...f, repo: e.target.value }))}
                    className="font-mono text-xs"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Teams team name</span>
                  <Input
                    value={String(form.teamsTeamName || '')}
                    onChange={(e) => setForm((f) => ({ ...f, teamsTeamName: e.target.value }))}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Group chat name</span>
                  <Input
                    value={String(form.groupChatName || '')}
                    onChange={(e) => setForm((f) => ({ ...f, groupChatName: e.target.value }))}
                  />
                </label>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Bot</span>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={Boolean(form.bot?.enabled)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        bot: { ...(f.bot || {}), enabled: e.target.checked, displayName: f.bot?.displayName || '' },
                      }))
                    }
                    className="rounded border-gray-300"
                  />
                  Bot enabled
                </label>
                <Input
                  placeholder="Bot display name"
                  value={String(form.bot?.displayName || '')}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      bot: { ...(f.bot || { enabled: false }), displayName: e.target.value },
                    }))
                  }
                />
              </div>
            </div>
          )}

          {mode === 'yaml' && !loading && (
            <div className="bg-gray-900 rounded-lg overflow-hidden border-2 border-gray-700 focus-within:border-blue-500">
              <div className="max-h-[min(560px,55vh)] overflow-auto custom-scrollbar">
                <Editor
                  value={yamlContent}
                  onValueChange={(v: string) => {
                    setYamlContent(v);
                    try {
                      yaml.load(v);
                      setParseError(null);
                    } catch (err: any) {
                      setParseError(err.message);
                    }
                  }}
                  highlight={(code: string) => highlight(code, languages.yaml, 'yaml')}
                  padding={16}
                  style={{
                    fontFamily: '"Fira Code", "Fira Mono", monospace',
                    fontSize: 13,
                    backgroundColor: '#1e293b',
                    color: '#e2e8f0',
                    minHeight: 280,
                  }}
                  className="outline-none"
                />
              </div>
            </div>
          )}

          {mode === 'resolved' && !loading && (
            <div className="space-y-4 text-sm min-h-[200px]">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The stream holds full workflow type definitions; each selected product/team/tenant row{' '}
                <span className="font-medium text-gray-600 dark:text-gray-300">enables</span> a subset (via{' '}
                <span className="font-mono text-[11px]">taxonomyWorkflowTypeIds</span> or inline{' '}
                <span className="font-mono text-[11px]">taxonomy.workflowTypes</span>). Resolved taxonomy is those
                enabled ids intersected across dimensions, then stream definitions merged with overlays from those
                rows and this role. Tools/prompts still merge from the stream plus scoped rows. With{' '}
                <span className="font-medium text-gray-600 dark:text-gray-300">no</span> scope ids, stream-wide
                defaults are omitted — only what the role YAML defines. Updates after you pause typing.
              </p>
              {!loading && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                  Preview scope:{' '}
                  {productIds.some((s) => s.trim()) || teamIds.some((s) => s.trim()) || tenantCodes.some((s) => s.trim())
                    ? `products=[${productIds.map((s) => s.trim()).filter(Boolean).join(', ')}] teams=[${teamIds.map((s) => s.trim()).filter(Boolean).join(', ')}] tenants=[${tenantCodes.map((s) => s.trim()).filter(Boolean).join(', ')}]`
                    : 'none (stream defaults omitted)'}
                </p>
              )}
              {resolvedLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              )}
              {resolvedError && (
                <p className="text-sm text-red-600 dark:text-red-400">{resolvedError}</p>
              )}
              {!resolvedLoading && !resolvedError && resolved && (
                <>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Resolved tools</h3>
                    {!overlayTools?.length ? (
                      <p className="text-xs text-gray-500">No tools in merged result.</p>
                    ) : (
                      <ul className="space-y-2">
                        {overlayTools.map((t: any) => (
                          <li
                            key={t.toolId}
                            className="border border-gray-200 dark:border-gray-700 rounded-md p-2.5 text-xs bg-white dark:bg-gray-900/40"
                          >
                            <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">{t.toolId}</div>
                            <div className="text-gray-600 dark:text-gray-400 mt-0.5">
                              enabled: {String(t.enabled)} · mode: {String(t.mode || 'overlay')}
                            </div>
                            {Array.isArray(t.capabilities) && t.capabilities.length > 0 && (
                              <div className="mt-1 text-gray-700 dark:text-gray-300">
                                capabilities: {t.capabilities.join(', ')}
                              </div>
                            )}
                            {t.config && typeof t.config === 'object' && Object.keys(t.config).length > 0 && (
                              <pre className="mt-2 text-[11px] whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-950/50 rounded p-2 max-h-40 overflow-y-auto">
                                {JSON.stringify(t.config, null, 2)}
                              </pre>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Resolved taxonomy</h3>
                    <ResolvedWorkflowTaxonomyReadonly workflowTypes={(overlayTax?.workflowTypes as any[]) || []} />
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Resolved tools by workflow type
                    </h3>
                    {!overlayEffectiveToolsByWorkflowType || Object.keys(overlayEffectiveToolsByWorkflowType).length === 0 ? (
                      <p className="text-xs text-gray-500">No workflow-type specific effective toolset available.</p>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(overlayEffectiveToolsByWorkflowType).map(([wtId, wtTools]) => (
                          <div
                            key={wtId}
                            className="border border-gray-200 dark:border-gray-700 rounded-md p-2.5 text-xs bg-white dark:bg-gray-900/40"
                          >
                            <div className="font-mono font-semibold text-gray-900 dark:text-gray-100">{wtId}</div>
                            {!Array.isArray(wtTools) || wtTools.length === 0 ? (
                              <p className="text-gray-500 mt-1">No tools.</p>
                            ) : (
                              <ul className="mt-1 space-y-1">
                                {wtTools.map((t: any) => (
                                  <li key={`${wtId}-${String(t.toolId)}`} className="text-gray-700 dark:text-gray-300">
                                    {String(t.toolId)} · enabled: {String(t.enabled)} · mode: {String(t.mode || 'overlay')}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || loading || !!parseError || mode === 'resolved'}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
